import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTRPCAPI } from '../../../../trpc/trpc';
import type { InboxPage, ReplyRow } from '../outreachTypes';
import { useRetry } from './useRetry';

export interface InboxFilters {
  campaignUuid: string | null;
}

/**
 * `outreach.inbox.list` (design outreach-v2 m1): every reply, newest first; the list groups them by
 * `row.group`. `outreach.reply.received` reads it again.
 */
export function useInbox(accountId: number, filters: InboxFilters) {
  const trpcAPI = useTRPCAPI();
  const query = useInfiniteQuery(
    trpcAPI.outreach.inbox.list.infiniteQueryOptions(
      { accountId, campaignUuid: filters.campaignUuid },
      { getNextPageParam: (page: InboxPage) => page.nextCursor ?? undefined, retry: false }
    )
  );
  const pages = query.data?.pages as InboxPage[] | undefined;
  const rows = useMemo<ReplyRow[]>(() => (pages ?? []).flatMap((page) => page.rows), [pages]);
  const retry = useRetry(query.refetch);
  return {
    rows,
    counts: pages?.[0]?.counts ?? null,
    isLoading: query.isLoading,
    error: query.error,
    retry,
    hasMore: query.hasNextPage,
    loadMore: query.fetchNextPage,
  };
}
